/**
 * QAISignupPage (F2) - v5.2 One MT5 Account Per User
 * Google OAuth signup page for Q AI standalone users
 *
 * Route: /q-ai/signup (public)
 * Target: New retail MT5 traders signing up for Q AI
 *
 * v5.2 Changes (One MT5 Account Per User):
 * - Check if user already has a bound MT5 account
 * - Show graceful popup if trying to connect different MT5 account
 * - First-time users: automatically bind MT5 account
 * - Prevents one email from linking multiple MT5 accounts
 *
 * v5.1 Changes (MT5-Only Flow):
 * - MT5-only users → /q-ai/mt5-ready (dedicated MT5 setup page)
 * - Web-only users → /q-ai/coach (web dashboard)
 * - Both selected → /q-ai/coach (web dashboard with MT5 integration)
 * - Clear separation: MT5 users chat on MT5, not web
 *
 * v5.0 Changes (QBridge Team Handoff):
 * - Smart QBridge detection after OAuth success
 * - Auto-start QBridge via qbridge:// protocol if installed
 * - Only show download prompt if QBridge not installed
 * - Seamless flow: Sign up → Auto-connect → Start trading
 *
 * v4.0 Changes:
 * - 40% larger fonts across all text
 * - Removed excessive gaps and whitespace
 * - Footer badges integrated into main content
 * - Consistent typography scale
 * - Mobile-first responsive design
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Grid,
  LinearProgress,
  Dialog,
  DialogContent,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import LanguageIcon from '@mui/icons-material/Language';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { GoogleLogin } from '@react-oauth/google';
import QAILogo from '../../Components/QAI/QAILogo';
import { googleLogin, checkMT5AccountBinding, bindMT5Account } from '../../api/qaiApi';
import { smartConnectQBridge } from '../../services/qbridge.service';
import { QBRIDGE_DOWNLOAD_URL, QBRIDGE_VERSION, QBRIDGE_SIZE } from '../../config';

// ═══════════════════════════════════════════════════════════════════════════════
// BRAND CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════

const BRAND = {
  primary: '#27B56D',
  primaryLight: '#22C55E',
  background: '#0A0A0F',
  surface: '#12121A',
  surfaceHover: '#1a1a24',
  text: '#FFFFFF',
  textSecondary: '#E2E8F0',
  textMuted: '#94A3B8',
  textDim: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)',
  borderLight: 'rgba(255, 255, 255, 0.15)',
  error: '#EF4444',
  cyan: '#3DEAFF'
};

// ═══════════════════════════════════════════════════════════════════════════════
// PRODUCT DATA
// ═══════════════════════════════════════════════════════════════════════════════

const PRODUCTS = [
  {
    id: 'mt5',
    name: 'Q.AI for MT5',
    badge: 'Recommended',
    icon: DesktopWindowsIcon,
    desc: 'On-chart AI coach for PMEX & international MT5',
    note: 'Downloads QBridge after signup',
    tags: ['Live Positions', 'Auto-Sync', 'Trade Analysis']
  },
  {
    id: 'web',
    name: 'Q.AI Coach (Web)',
    badge: null,
    icon: LanguageIcon,
    desc: 'Browser-based analytics, no installation',
    note: 'Goes directly to web dashboard',
    tags: ['Chart Analysis', 'Strategy', 'Risk Insights']
  }
];

// ═══════════════════════════════════════════════════════════════════════════════
// QBRIDGE CONNECTION STATES
// ═══════════════════════════════════════════════════════════════════════════════
const CONNECTION_STATES = {
  IDLE: 'idle',
  CHECKING: 'checking',
  STARTING: 'starting',
  CONNECTED: 'connected',
  NEEDS_DOWNLOAD: 'needs_download',
  ACCOUNT_MISMATCH: 'account_mismatch'  // v5.2: Different MT5 account detected
};

/**
 * QAISignupPage Component - v5.0 Smart QBridge Detection
 */
const QAISignupPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState({ mt5: true, web: false });

  // v5.0: QBridge connection state
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.IDLE);
  const [connectionMessage, setConnectionMessage] = useState('');
  const [qbridgeResult, setQbridgeResult] = useState(null);
  const [jwtToken, setJwtToken] = useState(null);

  // v5.2: MT5 Account binding state
  const [accountMismatchData, setAccountMismatchData] = useState(null);
  const [showAccountMismatchDialog, setShowAccountMismatchDialog] = useState(false);

  const hasProductSelected = selectedProducts.mt5 || selectedProducts.web;
  const needsMT5 = selectedProducts.mt5;
  const needsWebOnly = selectedProducts.web && !selectedProducts.mt5;
  const needsBoth = selectedProducts.mt5 && selectedProducts.web;

  /**
   * v5.2: Smart QBridge connection flow with account binding validation
   * 1. Check if QBridge running → send token
   * 2. Try qbridge://start protocol → wait → send token
   * 3. Check if detected MT5 account matches user's bound account
   * 4. If mismatch → show graceful popup
   * 5. If first time → bind the account
   * 6. Show download button only if all fails
   */
  const connectQBridge = async (token) => {
    console.log('[QAISignupPage] Starting QBridge connection flow...');
    setConnectionState(CONNECTION_STATES.CHECKING);
    setConnectionMessage('Checking for QBridge...');

    try {
      console.log('[QAISignupPage] Calling smartConnectQBridge...');
      const result = await smartConnectQBridge(token);
      console.log('[QAISignupPage] smartConnectQBridge result:', result);
      setQbridgeResult(result);

      if (result.connected) {
        console.log('[QAISignupPage] QBridge connected! Checking MT5 account binding...');

        // v5.2: Get detected MT5 account from install info
        const detectedAccount = result.installInfo?.mt5?.account || null;
        const detectedBroker = result.installInfo?.mt5?.broker || null;

        if (detectedAccount) {
          console.log('[QAISignupPage] Detected MT5 account:', detectedAccount);

          // Check if user has a bound account and if it matches
          const bindingStatus = await checkMT5AccountBinding(token, detectedAccount);
          console.log('[QAISignupPage] Account binding status:', bindingStatus);

          if (bindingStatus.hasBoundAccount && !bindingStatus.isMatch) {
            // Account mismatch - show graceful popup
            console.log('[QAISignupPage] Account mismatch detected!');
            setAccountMismatchData({
              detectedAccount,
              detectedBroker,
              boundAccount: bindingStatus.boundAccount,
              boundBroker: bindingStatus.boundBroker
            });
            setConnectionState(CONNECTION_STATES.ACCOUNT_MISMATCH);
            setShowAccountMismatchDialog(true);
            return;
          }

          if (!bindingStatus.hasBoundAccount && bindingStatus.canBind) {
            // First time - bind the account
            console.log('[QAISignupPage] First time user - binding MT5 account...');
            const bindResult = await bindMT5Account(token, detectedAccount, detectedBroker);
            console.log('[QAISignupPage] Bind result:', bindResult);
          }
        }

        // Proceed with normal flow
        setConnectionState(CONNECTION_STATES.CONNECTED);
        setConnectionMessage(result.autoStarted
          ? 'QBridge started and connected!'
          : 'Connected to QBridge!');

        // v5.1: Route based on product selection
        // MT5-only → /q-ai/mt5-ready (chat on MT5, not web)
        // Both selected → /q-ai/coach (web dashboard with MT5 integration)
        const targetRoute = needsBoth ? '/q-ai/coach' : '/q-ai/mt5-ready';
        console.log('[QAISignupPage] Redirecting to:', targetRoute, '(needsBoth:', needsBoth, ')');

        setTimeout(() => {
          navigate(targetRoute);
        }, 1500);
      } else if (result.needsDownload) {
        console.log('[QAISignupPage] QBridge needs download. Setting NEEDS_DOWNLOAD state...');
        setConnectionState(CONNECTION_STATES.NEEDS_DOWNLOAD);
        setConnectionMessage('QBridge not found. Download to connect MT5.');
      } else {
        console.log('[QAISignupPage] QBridge connection failed. Setting NEEDS_DOWNLOAD state...');
        setConnectionState(CONNECTION_STATES.NEEDS_DOWNLOAD);
        setConnectionMessage('Could not connect to QBridge.');
      }
    } catch (err) {
      console.error('[QAISignupPage] QBridge connection error:', err);
      setConnectionState(CONNECTION_STATES.NEEDS_DOWNLOAD);
      setConnectionMessage('Connection check failed. Please download QBridge.');
    }
  };

  /**
   * v5.2: Handle account mismatch - use the linked account
   * Closes popup and redirects to MT5 ready page with instruction to log into correct MT5
   */
  const handleUseLinkedAccount = () => {
    setShowAccountMismatchDialog(false);
    setConnectionState(CONNECTION_STATES.IDLE);
    // Go to MT5 ready page - user needs to log into their linked MT5 account
    navigate('/q-ai/mt5-ready', {
      state: {
        accountMismatch: true,
        linkedAccount: accountMismatchData?.boundAccount,
        linkedBroker: accountMismatchData?.boundBroker
      }
    });
  };

  /**
   * v5.2: Handle contact support from account mismatch popup
   */
  const handleContactSupport = () => {
    // Open support email
    window.open('mailto:info@qforgex.com?subject=MT5 Account Linking Issue&body=Hi Support Team,%0D%0A%0D%0AI need assistance changing my linked MT5 account.%0D%0A%0D%0ACurrent linked account: ' + (accountMismatchData?.boundAccount || 'Unknown') + '%0D%0ANew account I want to use: ' + (accountMismatchData?.detectedAccount || 'Unknown') + '%0D%0A%0D%0AThank you.', '_blank');
  };

  /**
   * v5.2: Close account mismatch dialog and reset state
   */
  const handleCloseAccountMismatch = () => {
    setShowAccountMismatchDialog(false);
    setConnectionState(CONNECTION_STATES.IDLE);
    setAccountMismatchData(null);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    console.log('[QAISignupPage] Google OAuth success, selectedProducts:', selectedProducts);
    console.log('[QAISignupPage] needsMT5:', needsMT5, 'hasProductSelected:', hasProductSelected);

    if (!hasProductSelected) {
      setError('Please select at least one Q.AI product.');
      return;
    }
    if (!agreedToTerms) {
      setError('Please accept the Terms of Service to continue.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const { token, user } = await googleLogin(credentialResponse.credential, 'qai');
      console.log('[QAISignupPage] Login successful, user:', user?.email);
      localStorage.setItem('qai_token', token);
      localStorage.setItem('qai_user', JSON.stringify(user));
      setJwtToken(token);

      // v5.0: Smart routing based on product selection
      if (needsMT5) {
        console.log('[QAISignupPage] MT5 selected - starting QBridge connection flow...');
        // User selected MT5 - attempt QBridge connection
        setIsLoading(false);
        await connectQBridge(token);
      } else {
        console.log('[QAISignupPage] Web only - redirecting to coach...');
        // Web only - go directly to coach
        navigate('/q-ai/coach');
      }
    } catch (err) {
      console.error('[QAISignupPage] Google signup error:', err);
      setError(err.message || 'Signup failed. Please try again.');
      setIsLoading(false);
    }
  };

  /**
   * Handle download button click
   */
  const handleDownload = () => {
    window.open(QBRIDGE_DOWNLOAD_URL, '_blank');
  };

  /**
   * Retry QBridge connection after download
   */
  const handleRetryConnection = async () => {
    if (jwtToken) {
      await connectQBridge(jwtToken);
    }
  };

  /**
   * Skip QBridge and go to web dashboard
   * v5.1: If MT5-only was selected, go to mt5-ready page instead
   */
  const handleSkipToWeb = () => {
    // If user selected both or web-only, go to coach
    // If MT5-only but skipping download, still show MT5 setup page
    const targetRoute = needsBoth || needsWebOnly ? '/q-ai/coach' : '/q-ai/mt5-ready';
    navigate(targetRoute);
  };

  const handleGoogleError = () => {
    setError('Google sign-in failed. Please try again.');
    setIsLoading(false);
  };

  /**
   * DEBUG: Test QBridge detection flow without OAuth
   * This simulates a successful login and triggers the QBridge detection overlay
   * Remove this function in production
   */
  const handleTestQBridgeFlow = async () => {
    console.log('[QAISignupPage] DEBUG: Testing QBridge flow without OAuth...');

    // Simulate setting a test token (use existing token if available)
    const testToken = localStorage.getItem('qai_token') || 'test-debug-token';
    setJwtToken(testToken);

    // Trigger the QBridge connection flow
    await connectQBridge(testToken);
  };

  const toggleProduct = (id) => {
    setSelectedProducts(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <Box
      sx={{
        backgroundColor: BRAND.background,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Glow */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '900px',
          height: '600px',
          background: `radial-gradient(ellipse, rgba(39, 181, 109, 0.05) 0%, transparent 60%)`,
          pointerEvents: 'none'
        }}
      />

      {/* ═══════════════════════════════════════════════════════════════════════════
          v5.0: QBridge Connection Overlay - Smart Detection Flow
          Shows progress during QBridge detection and provides download if needed
          ═══════════════════════════════════════════════════════════════════════════ */}
      {connectionState !== CONNECTION_STATES.IDLE && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(10, 10, 15, 0.95)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(8px)'
          }}
        >
          <Box
            sx={{
              backgroundColor: BRAND.surface,
              borderRadius: '20px',
              border: `1px solid ${BRAND.border}`,
              p: { xs: 3, sm: 4 },
              maxWidth: 480,
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)'
            }}
          >
            {/* Animated Logo */}
            <Box sx={{ mb: 3 }}>
              <QAILogo size="lg" animated={connectionState === CONNECTION_STATES.CHECKING || connectionState === CONNECTION_STATES.STARTING} />
            </Box>

            {/* ─────────────────────────────────────────────────────────────────────
                State: CHECKING / STARTING - Show progress animation
                ───────────────────────────────────────────────────────────────────── */}
            {(connectionState === CONNECTION_STATES.CHECKING || connectionState === CONNECTION_STATES.STARTING) && (
              <>
                <Typography
                  sx={{
                    color: BRAND.text,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    fontWeight: 700,
                    mb: 1
                  }}
                >
                  {connectionState === CONNECTION_STATES.CHECKING ? 'Detecting QBridge...' : 'Starting QBridge...'}
                </Typography>
                <Typography sx={{ color: BRAND.textSecondary, fontSize: '1rem', mb: 3 }}>
                  {connectionMessage}
                </Typography>
                <LinearProgress
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(39, 181, 109, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: BRAND.primary,
                      borderRadius: 3
                    }
                  }}
                />
                <Typography sx={{ color: BRAND.textMuted, fontSize: '0.875rem', mt: 2 }}>
                  This may take a few seconds...
                </Typography>
              </>
            )}

            {/* ─────────────────────────────────────────────────────────────────────
                State: CONNECTED - Show success with auto-redirect
                ───────────────────────────────────────────────────────────────────── */}
            {connectionState === CONNECTION_STATES.CONNECTED && (
              <>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(39, 181, 109, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    animation: 'successPulse 1s ease-out'
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 48, color: BRAND.primary }} />
                </Box>
                <Typography
                  sx={{
                    color: BRAND.primary,
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                    fontWeight: 800,
                    mb: 1
                  }}
                >
                  Connected!
                </Typography>
                <Typography sx={{ color: BRAND.textSecondary, fontSize: '1rem', mb: 2 }}>
                  {connectionMessage}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: BRAND.primary }} />
                  <Typography sx={{ color: BRAND.textMuted, fontSize: '0.875rem' }}>
                    Redirecting to Q.AI Coach...
                  </Typography>
                </Box>
              </>
            )}

            {/* ─────────────────────────────────────────────────────────────────────
                State: NEEDS_DOWNLOAD - Show download button with options
                ───────────────────────────────────────────────────────────────────── */}
            {connectionState === CONNECTION_STATES.NEEDS_DOWNLOAD && (
              <>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(245, 158, 11, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  <DownloadIcon sx={{ fontSize: 40, color: '#F59E0B' }} />
                </Box>
                <Typography
                  sx={{
                    color: BRAND.text,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    fontWeight: 700,
                    mb: 1
                  }}
                >
                  Install QBridge
                </Typography>
                <Typography sx={{ color: BRAND.textSecondary, fontSize: '1rem', mb: 0.5 }}>
                  {connectionMessage}
                </Typography>
                <Typography sx={{ color: BRAND.textMuted, fontSize: '0.875rem', mb: 3 }}>
                  QBridge connects Q.AI to your MT5 platform for live position tracking.
                </Typography>

                {/* Download Button - Primary CTA */}
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  sx={{
                    backgroundColor: BRAND.primary,
                    color: '#000',
                    fontWeight: 700,
                    fontSize: '1rem',
                    py: 1.5,
                    borderRadius: '10px',
                    textTransform: 'none',
                    mb: 1.5,
                    '&:hover': {
                      backgroundColor: BRAND.primaryLight,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 8px 24px rgba(39, 181, 109, 0.3)`
                    }
                  }}
                >
                  Download QBridge ({QBRIDGE_SIZE})
                </Button>

                {/* Version info */}
                <Typography sx={{ color: BRAND.textDim, fontSize: '0.75rem', mb: 2 }}>
                  Version {QBRIDGE_VERSION} • Windows 10/11
                </Typography>

                {/* Secondary Actions */}
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<LinkIcon />}
                    onClick={handleRetryConnection}
                    sx={{
                      borderColor: BRAND.borderLight,
                      color: BRAND.textSecondary,
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: BRAND.primary,
                        backgroundColor: 'rgba(39, 181, 109, 0.08)',
                        color: BRAND.primary
                      }
                    }}
                  >
                    Retry Connection
                  </Button>
                  {/* v5.1: Only show "Skip to Web" if user selected Web option */}
                  {/* MT5-only users should NOT see web dashboard option */}
                  {(needsBoth || needsWebOnly) && (
                    <Button
                      variant="text"
                      onClick={handleSkipToWeb}
                      sx={{
                        color: BRAND.textMuted,
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        px: 2,
                        py: 1,
                        borderRadius: '8px',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          color: BRAND.textSecondary
                        }
                      }}
                    >
                      Skip to Web Dashboard →
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Compact Header */}
      <Box sx={{
        px: { xs: 2, sm: 3 },
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        zIndex: 1
      }}>
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 20 }} />}
          onClick={() => navigate('/q-ai')}
          sx={{
            color: BRAND.textMuted,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            minWidth: 'auto',
            '&:hover': { color: BRAND.text, backgroundColor: 'transparent' }
          }}
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: BRAND.textDim }}>
          <SecurityIcon sx={{ fontSize: 16, color: BRAND.primary }} />
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>Bank-Grade Security</Typography>
        </Box>
      </Box>

      {/* Main Content - Reduced top padding */}
      <Container
        maxWidth="md"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          pt: { xs: 0, sm: 0 },
          pb: { xs: 1.5, sm: 2 },
          px: { xs: 2, sm: 3 },
          position: 'relative',
          zIndex: 1
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 720, mx: 'auto' }}>

          {/* Logo + Title Row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 1.5 }}>
            <QAILogo size="medium" />
            <Box sx={{ textAlign: 'left' }}>
              <Typography
                variant="h1"
                sx={{
                  color: BRAND.text,
                  fontSize: { xs: '1.75rem', sm: '2.25rem' },
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1
                }}
              >
                Create Your Q.AI Account
              </Typography>
              <Typography sx={{ color: BRAND.textSecondary, fontSize: { xs: '1rem', sm: '1.125rem' }, mt: 0.5, fontWeight: 500 }}>
                AI-powered trading intelligence • 50 free tokens • No credit card
              </Typography>
            </Box>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: BRAND.error,
                borderRadius: '10px',
                border: `1px solid rgba(239, 68, 68, 0.2)`,
                fontSize: '1rem',
                '& .MuiAlert-icon': { color: BRAND.error }
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Product Selection Card */}
          <Box
            sx={{
              backgroundColor: BRAND.surface,
              borderRadius: '14px',
              border: `1px solid ${BRAND.border}`,
              p: { xs: 1.5, sm: 2 },
              mb: 1.5
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Typography sx={{ color: BRAND.text, fontSize: { xs: '1.125rem', sm: '1.25rem' }, fontWeight: 700 }}>
                Choose Your Q.AI Experience
              </Typography>
              <Typography sx={{ color: BRAND.textMuted, fontSize: '0.875rem', fontWeight: 500 }}>
                — Select one or both
              </Typography>
            </Box>

            <Grid container spacing={1}>
              {PRODUCTS.map((product) => {
                const Icon = product.icon;
                const isSelected = selectedProducts[product.id];
                return (
                  <Grid item xs={12} sm={6} key={product.id}>
                    <Box
                      onClick={() => toggleProduct(product.id)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        p: { xs: 1.25, sm: 1.5 },
                        borderRadius: '10px',
                        border: `2px solid ${isSelected ? BRAND.primary : BRAND.border}`,
                        backgroundColor: isSelected ? 'rgba(39, 181, 109, 0.08)' : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        height: '100%',
                        '&:hover': {
                          borderColor: isSelected ? BRAND.primary : BRAND.borderLight,
                          backgroundColor: isSelected ? 'rgba(39, 181, 109, 0.1)' : BRAND.surfaceHover
                        }
                      }}
                    >
                      {/* Header Row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          checked={isSelected}
                          sx={{
                            color: BRAND.textDim,
                            '&.Mui-checked': { color: BRAND.primary },
                            '& .MuiSvgIcon-root': { fontSize: 26 },
                            p: 0
                          }}
                        />
                        <Icon sx={{ color: BRAND.primary, fontSize: 24 }} />
                        <Typography sx={{ color: BRAND.text, fontSize: '1.125rem', fontWeight: 700, flex: 1 }}>
                          {product.name}
                        </Typography>
                        {product.badge && (
                          <Typography
                            sx={{
                              fontSize: '0.6875rem',
                              color: BRAND.primary,
                              backgroundColor: 'rgba(39, 181, 109, 0.15)',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: '4px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.02em'
                            }}
                          >
                            {product.badge}
                          </Typography>
                        )}
                      </Box>

                      {/* Description */}
                      <Typography sx={{ color: BRAND.textSecondary, fontSize: '1rem', lineHeight: 1.4, pl: 4.5 }}>
                        {product.desc}
                      </Typography>

                      {/* Note - Higher contrast for visibility */}
                      <Typography sx={{ color: BRAND.textMuted, fontSize: '0.9375rem', pl: 4.5 }}>
                        <Box component="span" sx={{ color: BRAND.primary, mr: 0.5 }}>→</Box>
                        {product.note}
                      </Typography>

                      {/* Tags */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 4.5, mt: 0.25 }}>
                        {product.tags.map((tag) => (
                          <Typography
                            key={tag}
                            sx={{
                              fontSize: '0.75rem',
                              color: BRAND.cyan,
                              backgroundColor: 'rgba(61, 234, 255, 0.1)',
                              px: 0.75,
                              py: 0.25,
                              borderRadius: '4px',
                              fontWeight: 600
                            }}
                          >
                            {tag}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {!hasProductSelected && (
              <Typography sx={{ color: BRAND.error, fontSize: '0.9375rem', mt: 1.5, fontWeight: 600 }}>
                Please select at least one product
              </Typography>
            )}
          </Box>

          {/* DEBUG: Test QBridge Flow Button - Remove in production */}
          {process.env.NODE_ENV === 'development' && needsMT5 && (
            <Box sx={{
              mb: 1.5,
              p: 1.5,
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              border: '1px dashed rgba(245, 158, 11, 0.4)',
              borderRadius: '10px'
            }}>
              <Typography sx={{ color: '#F59E0B', fontSize: '0.75rem', fontWeight: 600, mb: 1 }}>
                🔧 DEV MODE: OAuth not working? Test QBridge detection directly:
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={handleTestQBridgeFlow}
                sx={{
                  backgroundColor: '#F59E0B',
                  color: '#000',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  py: 1,
                  borderRadius: '8px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#D97706'
                  }
                }}
              >
                Test QBridge Detection Flow (Skip OAuth)
              </Button>
            </Box>
          )}

          {/* Auth Buttons Row - Equal width buttons */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5,
            mb: 1.5
          }}>
            {/* Google Button - Same width as Email button */}
            <Box sx={{
              height: '48px',
              '& > div': {
                width: '100%',
                height: '100%',
                '& > div': {
                  width: '100% !important',
                  height: '100% !important',
                  '& iframe': {
                    width: '100% !important',
                    height: '48px !important'
                  }
                }
              }
            }}>
              {isLoading ? (
                <Button
                  fullWidth
                  variant="contained"
                  disabled
                  sx={{
                    height: '48px',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    color: 'rgba(0, 0, 0, 0.5)',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: '8px',
                    boxShadow: 'none'
                  }}
                >
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Creating account...
                </Button>
              ) : (
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  theme="filled_blue"
                  size="large"
                  text="continue_with"
                  shape="rectangular"
                />
              )}
            </Box>

            {/* Email Sign In - Exact same height as Google button */}
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate('/q-ai/login')}
              sx={{
                height: '48px',
                borderColor: BRAND.borderLight,
                color: BRAND.text,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: '8px',
                '&:hover': {
                  borderColor: BRAND.primary,
                  backgroundColor: 'rgba(39, 181, 109, 0.08)'
                }
              }}
            >
              Sign in with Email
            </Button>
          </Box>

          {/* Terms + Platform Login + Trust Badges - All in One Row */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1.5, md: 2 },
            py: 1.5,
            px: { xs: 1.5, sm: 2 },
            backgroundColor: BRAND.surface,
            borderRadius: '12px',
            border: `1px solid ${BRAND.border}`
          }}>
            {/* Terms Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  sx={{
                    color: BRAND.textDim,
                    '&.Mui-checked': { color: BRAND.primary },
                    '& .MuiSvgIcon-root': { fontSize: 22 },
                    p: 0,
                    mr: 1
                  }}
                />
              }
              label={
                <Typography sx={{ color: BRAND.textSecondary, fontSize: '0.9375rem', fontWeight: 500 }}>
                  I agree to the{' '}
                  <Box component="a" href="/terms" target="_blank" sx={{ color: BRAND.primary, textDecoration: 'none', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}>
                    Terms
                  </Box>
                  {' & '}
                  <Box component="a" href="/privacy" target="_blank" sx={{ color: BRAND.primary, textDecoration: 'none', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}>
                    Privacy Policy
                  </Box>
                </Typography>
              }
              sx={{ m: 0 }}
            />

            {/* Trust Badges - Moved from footer */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}>
              {[
                { icon: <CheckCircleIcon sx={{ fontSize: 18 }} />, text: '50 Free Tokens' },
                { icon: <CheckCircleIcon sx={{ fontSize: 18 }} />, text: 'No Credit Card' },
                { icon: <CheckCircleIcon sx={{ fontSize: 18 }} />, text: 'Instant Access' }
              ].map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ color: BRAND.primary, display: 'flex' }}>{item.icon}</Box>
                  <Typography sx={{ color: BRAND.textSecondary, fontSize: '0.875rem', fontWeight: 600 }}>{item.text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Bottom Links Row */}
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 0.75, sm: 2.5 },
            mt: 1.5
          }}>
            <Typography sx={{ color: BRAND.textMuted, fontSize: '1rem', fontWeight: 500 }}>
              Already have a Q.AI account?{' '}
              <Box
                component="span"
                onClick={() => navigate('/q-ai/login')}
                sx={{ color: BRAND.primary, cursor: 'pointer', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}
              >
                Log in
              </Box>
            </Typography>
            <Typography sx={{ color: BRAND.textDim, display: { xs: 'none', sm: 'block' } }}>•</Typography>
            <Button
              size="small"
              onClick={() => navigate('/login')}
              sx={{
                color: BRAND.textMuted,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': { color: BRAND.primary, backgroundColor: 'transparent' }
              }}
            >
              QTradeView Platform Login →
            </Button>
          </Box>
        </Box>
      </Container>

      {/* ═══════════════════════════════════════════════════════════════════════════
          v5.2: Account Mismatch Dialog - Graceful popup when different MT5 detected
          ═══════════════════════════════════════════════════════════════════════════ */}
      <Dialog
        open={showAccountMismatchDialog}
        onClose={handleCloseAccountMismatch}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: BRAND.surface,
            borderRadius: '16px',
            border: `1px solid ${BRAND.border}`,
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.5)'
          }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Close Button */}
          <IconButton
            onClick={handleCloseAccountMismatch}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: BRAND.textMuted,
              '&:hover': { color: BRAND.text, backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            {/* Warning Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'rgba(245, 158, 11, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2.5
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 44, color: '#F59E0B' }} />
            </Box>

            {/* Title */}
            <Typography
              sx={{
                color: BRAND.text,
                fontSize: { xs: '1.375rem', sm: '1.5rem' },
                fontWeight: 800,
                mb: 1.5,
                lineHeight: 1.2
              }}
            >
              Different MT5 Account Detected
            </Typography>

            {/* Description */}
            <Typography sx={{ color: BRAND.textSecondary, fontSize: '1rem', mb: 3, lineHeight: 1.5 }}>
              Your Q.AI account is already linked to a different MT5 account. Each email can only be linked to one MT5 account for security.
            </Typography>

            {/* Account Comparison */}
            <Box
              sx={{
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: '12px',
                p: 2.5,
                mb: 3
              }}
            >
              {/* Currently Detected */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ color: BRAND.textMuted, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                  Currently Logged In
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                  <DesktopWindowsIcon sx={{ color: '#EF4444', fontSize: 20 }} />
                  <Typography sx={{ color: '#EF4444', fontSize: '1.125rem', fontWeight: 700 }}>
                    MT5 #{accountMismatchData?.detectedAccount || '—'}
                  </Typography>
                  {accountMismatchData?.detectedBroker && (
                    <Typography sx={{ color: BRAND.textMuted, fontSize: '0.875rem' }}>
                      ({accountMismatchData.detectedBroker})
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Divider */}
              <Box sx={{ borderTop: `1px dashed ${BRAND.border}`, my: 2 }} />

              {/* Linked Account */}
              <Box>
                <Typography sx={{ color: BRAND.textMuted, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                  Your Linked Account
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                  <DesktopWindowsIcon sx={{ color: BRAND.primary, fontSize: 20 }} />
                  <Typography sx={{ color: BRAND.primary, fontSize: '1.125rem', fontWeight: 700 }}>
                    MT5 #{accountMismatchData?.boundAccount || '—'}
                  </Typography>
                  {accountMismatchData?.boundBroker && (
                    <Typography sx={{ color: BRAND.textMuted, fontSize: '0.875rem' }}>
                      ({accountMismatchData.boundBroker})
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Primary: Use Linked Account */}
              <Button
                fullWidth
                variant="contained"
                onClick={handleUseLinkedAccount}
                sx={{
                  backgroundColor: BRAND.primary,
                  color: '#000',
                  fontWeight: 700,
                  fontSize: '1rem',
                  py: 1.5,
                  borderRadius: '10px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: BRAND.primaryLight,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 8px 24px rgba(39, 181, 109, 0.3)`
                  }
                }}
              >
                Log into MT5 #{accountMismatchData?.boundAccount || '—'}
              </Button>

              {/* Secondary: Contact Support */}
              <Button
                fullWidth
                variant="outlined"
                startIcon={<SupportAgentIcon />}
                onClick={handleContactSupport}
                sx={{
                  borderColor: BRAND.borderLight,
                  color: BRAND.textSecondary,
                  fontWeight: 600,
                  fontSize: '0.9375rem',
                  py: 1.25,
                  borderRadius: '10px',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: BRAND.primary,
                    backgroundColor: 'rgba(39, 181, 109, 0.08)',
                    color: BRAND.primary
                  }
                }}
              >
                Need to Change Linked Account? Contact Support
              </Button>
            </Box>

            {/* Help Text */}
            <Typography sx={{ color: BRAND.textDim, fontSize: '0.8125rem', mt: 2.5, lineHeight: 1.4 }}>
              Need to change your linked account? Contact our support team at{' '}
              <Box
                component="a"
                href="mailto:info@qforgex.com"
                sx={{ color: BRAND.primary, textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
              >
                info@qforgex.com
              </Box>
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default QAISignupPage;
